<script setup>

import IndexLayout from "@/Layouts/IndexLayout.vue";
import CardGame from "@/Components/CardGame.vue";
import Card from "@/Components/Card.vue";
import {computed, ref} from "vue";
import PaginateItems from "@/Components/PaginateItems.vue";
import FiltersAndSearch from "@/Pages/Category/FiltersAndSearch.vue";
import ChatWidget from "@/Components/CommunityChat/ChatWidget.vue";
import {Head, router, usePage} from "@inertiajs/vue3";
import debounce from "lodash-es/debounce.js";
import SearchResultEmpty from "@/Components/SearchResultEmpty.vue";
import Loading from "@/Components/Loading.vue";
import {route} from "ziggy-js";
import {trans} from "laravel-vue-i18n";

const page = usePage(),
    isLoading = ref(false),
    isLoadingMore = ref(false),
    searchValue = ref(route().params.search ?? ''),
    letter = ref(route().params.letter ?? ''),
    sortValue = ref(route().params.sort ?? null),
    searchValueShow = ref(!!route().params.search),
    currentPage = ref(Number(route().params.page ?? 1))

// Цифры и латиница в одну группу, кириллица - в другую (в разметке между ними стоит отбивка).
// Раньше здесь сравнивалось `'a' > letter.toLowerCase()`, что раскладывало ровно наоборот и
// бессмысленно: цифры (код меньше, чем у `a`) попадали в латинскую группу, а латиница вместе с
// кириллицей (U+0430 больше, чем U+0061) - в кириллическую.
const isLatin = (symbol) => /^[a-z0-9]$/i.test(symbol)
const arrLetters = computed(() => page.props?.product_letters ?? [])
const arrLatLetters = computed(() => arrLetters.value.filter(isLatin))
const arrRuLetters = computed(() => arrLetters.value.filter(symbol => !isLatin(symbol)))

const products = ref(page.props.products.data)

// Каждый запрос отправляет ПОЛНЫЙ набор параметров и строится от пути без query-строки.
// router.get(url, data) при GET сливает data в уже существующую query-строку url, а page.url её
// содержит - поэтому раньше параметр, однажды попавший в адрес, оттуда больше не исчезал:
// выбранная буква переживала любой последующий поиск (?letter=R&search=dota искал "dota" только
// среди игр на R и дальше по алфавиту), а сбросить её было нечем - управления "все буквы" нет.
const fetchPage = ({append = false} = {}) => {
    if (!append) {
        currentPage.value = 1
    }
    append ? isLoadingMore.value = true : isLoading.value = true

    router.get(page.url.split('?')[0], {
        search: searchValue.value || undefined,
        sort: sortValue.value || undefined,
        letter: letter.value || undefined,
        page: currentPage.value > 1 ? currentPage.value : undefined,
    }, {
        preserveState: true,
        preserveScroll: true,
        // onSuccess получает свежую страницу аргументом; чтение page.props из onFinish полагается
        // на то, что реактивный объект уже обновился, и молча отдаёт прошлую выдачу, если нет.
        onSuccess: (fresh) => {
            products.value = append
                ? [...products.value, ...fresh.props.products.data]
                : fresh.props.products.data
            searchValueShow.value = searchValue.value !== ''
        },
        onFinish: () => {
            isLoading.value = false
            isLoadingMore.value = false
        },
    })
}

// Дебаунсится только ввод текста. Клик по букве или по сортировке - это одно осознанное действие,
// и секунда ожидания на нём читается как "не работает".
const fetchDebounced = debounce(fetchPage, 500)

const updateSearchValue = (value) => {
    searchValue.value = value
    // Поиск и алфавит - два способа сузить один и тот же список, а не фильтры поверх друг друга:
    // держать их одновременно означает искать внутри одной буквы, чего никто не просил.
    letter.value = ''
    fetchDebounced()
}

const selectSymbol = (val) => {
    fetchDebounced.cancel()
    // Повторный клик по активной букве снимает фильтр - иначе выйти из него можно только
    // перезагрузкой страницы.
    letter.value = letter.value === val ? '' : val
    searchValue.value = ''
    searchValueShow.value = false
    fetchPage()
}

const updateSort = (event) => {
    fetchDebounced.cancel()
    sortValue.value = event
    fetchPage()
}

const hasMore = computed(() => currentPage.value < page.props.products.last_page)

const onShowMore = () => {
    currentPage.value++
    fetchPage({append: true})
}

// Task 5 (docs/plans/marketplace-catalog-visibility-2026-08-09.md): all seven categories used to
// share the site-wide default <meta description> - Category/Show.vue had no <Head> at all.
// meta_title/meta_description are optional per category; falls back to the plain title/no
// override (app.blade.php's own site-wide default takes over) when empty.
const pageTitle = computed(() => page.props.category?.meta_title
    || page.props.category?.title
    || trans('All categories'))
const metaDescription = computed(() => page.props.category?.meta_description || null)

</script>

<template>
    <IndexLayout :title="pageTitle">
        <Head v-if="metaDescription">
            <meta :content="metaDescription" name="description"/>
        </Head>
        <div
            class="overflow-hidden mx-auto lg:px-2 py-2.5 lg:py-6 flex flex-col gap-3 px-2.5 sm:max-w-[39rem] md:max-w-[47rem] lg:max-w-[60rem] xl:max-w-[76rem] 2xl:max-w-[87.5rem]">

            <h1 class="sr-only">{{ pageTitle }}</h1>

            <ChatWidget/>

            <FiltersAndSearch :search-value="searchValue"
                              @select-letter="selectSymbol($event)"
                              :arr-lat-letters="arrLatLetters"
                              :arr-ru-letters="arrRuLetters"
                              :active-letter="letter"
                              :active-sort="sortValue"
                              @update-sort="updateSort($event)"
                              @updateSearchValue="updateSearchValue($event)" :loading="isLoading"/>

            <div class="flex gap-2 grow">
                <transition
                    enter-active-class="ease-out duration-300"
                    enter-from-class="opacity-0 w-0"
                    enter-to-class="opacity-100 w-fit"
                    leave-active-class="ease-in duration-200 "
                    leave-from-class="opacity-100 w-fit"
                    leave-to-class="opacity-0 w-0">
                    <!-- Каждая буква кликабельна. Раньше кликабельными были только чётные по
                         индексу, а нечётные рисовались декоративной точкой без подписи - то есть
                         на мобильном ровно половина алфавита (включая R, а значит и Roblox) была
                         недоступна физически, при том что на десктопе та же буква работала. -->
                    <div v-show="searchValue === '' && (arrLatLetters.length > 0 || arrRuLetters.length > 0)"
                         class="flex flex-col gap-1 pt-1 lg:hidden overflow-hidden shrink-0 items-center">
                        <div v-if="letter"
                             @click="selectSymbol(letter)"
                             :aria-label="$t('Reset')"
                             class="text-xs leading-full hover:bg-blue-600 transition cursor-pointer font-normal p-1 rounded-full bg-blue-600 w-3 h-4 text-white flex items-center justify-center">
                            <span>×</span></div>
                        <template v-for="symbol in arrLatLetters">
                            <div
                                @click="selectSymbol(symbol)"
                                :class="letter === symbol ? 'bg-blue-600 dark:bg-blue-600 text-white dark:text-white' : 'bg-slate-600 dark:bg-gray-500 text-white dark:text-dark-blue'"
                                class="text-xs leading-full hover:bg-blue-600 transition cursor-pointer font-normal p-1 rounded-full w-3 h-4 flex items-center justify-center">
                                <span>{{ symbol }}</span></div>
                        </template>
                        <div class="w-3 h-1"></div>
                        <template v-for="symbol in arrRuLetters">
                            <div
                                @click="selectSymbol(symbol)"
                                :class="letter === symbol ? 'bg-blue-600 dark:bg-blue-600 text-white dark:text-white' : 'bg-slate-800 dark:bg-gray-400 text-white dark:text-dark-blue'"
                                class="text-xs leading-full hover:bg-blue-600 transition cursor-pointer font-normal p-1 rounded-full w-3 h-4 flex items-center justify-center">
                                <span>{{ symbol }}</span></div>
                        </template>
                    </div>
                </transition>
                <div class="grow">
                    <Card :class="{'lg:px-2.5': products.length > 0}"
                          class="grid dark:bg-dark-blue grow grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 lg:gap-2.5 overflow-hidden pb-2.5 lg:py-2.5 font-medium relative">
                        <div v-if="searchValueShow"
                             class="px-3.5 py-2.5 col-span-1 dark:text-slate-200 lg:col-span-2 xl:col-span-3 text-xs leading-120 font-bold">
                            {{ $t('Related results') }}:
                        </div>
                        <template v-for="product in products">
                            <CardGame :product="product"/>
                        </template>
                        <SearchResultEmpty :content-class="['col-span-1', 'lg:col-span-2', 'xl:col-span-3']"
                                           :is-empty-category="page.props.product_count === 0"
                                           v-if="!isLoading && products.length === 0"></SearchResultEmpty>
                        <Loading v-if="isLoading"/>
                    </Card>
                    <PaginateItems :has-more="hasMore"
                                   :loading="isLoadingMore"
                                   :on-show-more="onShowMore">
                    </PaginateItems>
                </div>
            </div>
        </div>
    </IndexLayout>
</template>


